import React, { useEffect, useRef, useState } from 'react';
import { Search, ChevronDown, X, Plus, Check, Loader2 } from 'lucide-react';
import { apiGet, apiPut } from '../lib/api';
import { toast } from 'sonner';

/**
 * Searchable Department picker with inline "+ Add new department".
 *
 * Reads + writes the canonical departments master via:
 *   GET  /api/user-management/departments     (union of master + in-use)
 *   PUT  /api/config-masters/departments      (overwrites the values list)
 *
 * Adding a new one persists it so every other dropdown across the ERP picks it
 * up immediately.
 *
 * Props:
 *   value      — currently selected department string
 *   onChange   — callback(newValue)
 *   required   — passes through to the input's required attribute
 *   testid     — data-testid root (input gets `${testid}-input`, options get `${testid}-option-<dept>`)
 *   disabled   — disables interaction
 *   className  — extra classes on the wrapper
 */
export default function DepartmentCombobox({
  value = '',
  onChange,
  required = false,
  testid,
  disabled = false,
  className = '',
}) {
  const [departments, setDepartments] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || '');
  const [savingNew, setSavingNew] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  // Load department list once; refresh whenever a new dept is added below.
  const loadDepartments = async () => {
    try {
      const r = await apiGet('/user-management/departments');
      setDepartments(Array.isArray(r?.departments) ? r.departments : []);
    } catch {
      setDepartments([]);
    }
  };
  useEffect(() => { loadDepartments(); }, []);
  useEffect(() => { setQuery(value || ''); }, [value]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = !q
    ? departments
    : departments.filter(d => d.toLowerCase().includes(q));

  // Show "+ Add" footer only if the typed text doesn't already exist (case-insensitive)
  const canAddNew = q && !departments.some(d => d.toLowerCase() === q);

  const commit = (dept) => {
    setQuery(dept);
    setOpen(false);
    onChange?.(dept);
  };

  const addNewDepartment = async () => {
    const newDept = query.trim();
    if (!newDept) return;
    setSavingNew(true);
    try {
      // Read current master, append, write back. The departments endpoint returns
      // a self-healing union — write to the master key so it persists.
      const current = await apiGet('/config-masters/departments').catch(() => ({ values: [] }));
      const merged = Array.from(new Set([...(current?.values || []), ...departments, newDept]))
        .filter(Boolean);
      await apiPut('/config-masters/departments', { values: merged });
      toast.success(`Added "${newDept}" to departments`);
      await loadDepartments();
      commit(newDept);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add department');
    } finally {
      setSavingNew(false);
    }
  };

  const handleKey = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[activeIdx]) commit(filtered[activeIdx]);
      else if (canAddNew) addNewDepartment();
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const clear = () => {
    setQuery('');
    onChange?.('');
    inputRef.current?.focus();
  };

  return (
    <div ref={rootRef} className={`relative ${className}`} data-testid={testid}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          required={required}
          disabled={disabled}
          onFocus={() => setOpen(true)}
          onChange={e => {
            const v = e.target.value;
            setQuery(v);
            setOpen(true);
            setActiveIdx(0);
            // Live update parent so form validation can react. If the typed
            // value matches an existing dept exactly, treat it as a commit.
            const exact = departments.find(d => d.toLowerCase() === v.trim().toLowerCase());
            onChange?.(exact || v);
          }}
          onKeyDown={handleKey}
          placeholder="Search or add department…"
          className="w-full pl-9 pr-9 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-[#1F7A6E] disabled:bg-slate-50"
          data-testid={testid ? `${testid}-input` : undefined}
          autoComplete="off"
        />
        {query ? (
          <button type="button" onClick={clear} disabled={disabled} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 disabled:opacity-40">
            <X size={14} />
          </button>
        ) : (
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        )}
      </div>

      {open && !disabled && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-y-auto" data-testid={testid ? `${testid}-dropdown` : undefined}>
          {filtered.length === 0 && !canAddNew && (
            <div className="px-3 py-4 text-xs text-slate-500 text-center">No departments yet.</div>
          )}
          {filtered.map((d, idx) => (
            <button
              key={d}
              type="button"
              onMouseEnter={() => setActiveIdx(idx)}
              onClick={() => commit(d)}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm ${idx === activeIdx ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
              data-testid={testid ? `${testid}-option-${d}` : undefined}
            >
              <span className="text-slate-800">{d}</span>
              {value === d && <Check size={14} className="text-emerald-600 flex-shrink-0" />}
            </button>
          ))}
          {canAddNew && (
            <button
              type="button"
              onClick={addNewDepartment}
              disabled={savingNew}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm border-t border-slate-200 bg-teal-50/40 hover:bg-teal-100/60 text-[#1F7A6E] font-semibold disabled:opacity-60"
              data-testid={testid ? `${testid}-add-new` : undefined}
            >
              {savingNew ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Add new department: <span className="font-bold">"{query.trim()}"</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
